import React from "react";
import { useTranslation } from "react-i18next";
import {
    Search,
    X,
    Folder,
    CheckSquare,
    LayoutGrid,
    ArrowUpDown,
} from "lucide-react";
import { Input } from "@shadcn-in-nx/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import Button from "../../../components/Button.jsx";

const SORT_OPTIONS = [
    { value: "name_asc", label: "Name A-Z" },
    { value: "name_desc", label: "Name Z-A" },
    { value: "date_desc", label: "Newest first" },
    { value: "date_asc", label: "Oldest first" },
    { value: "steps_desc", label: "Most steps" },
    { value: "executions_desc", label: "Most executions" },
];

export default function WorkflowToolbar({
    searchTerm,
    onSearchChange,
    onClearSearch,
    viewMode,
    onViewModeChange,
    sortBy,
    onSortChange,
    hideViewToggle = false,
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-wrap items-center gap-3 pb-4">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                    type="text"
                    placeholder={t("workflowIndex.search.placeholder")}
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10 pr-10"
                />
                {searchTerm && (
                    <button
                        onClick={onClearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            <Select value={sortBy} onValueChange={onSortChange}>
                <SelectTrigger className="w-[140px] sm:w-[160px]">
                    <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                    {SORT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {!hideViewToggle && (
                <div className="flex items-center gap-1 w-full sm:w-auto">
                    <Button
                        variant={viewMode === "list" ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => onViewModeChange("list")}
                        className="flex items-center gap-1.5"
                    >
                        <CheckSquare className="h-4 w-4" />
                        <span className="hidden sm:inline">{t("workflowIndex.viewMode.list")}</span>
                    </Button>
                    <Button
                        variant={viewMode === "cards" ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => onViewModeChange("cards")}
                        className="flex items-center gap-1.5"
                    >
                        <LayoutGrid className="h-4 w-4" />
                        <span className="hidden sm:inline">{t("workflowIndex.viewMode.cards")}</span>
                    </Button>
                    <Button
                        variant={viewMode === "grouped" ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => onViewModeChange("grouped")}
                        className="flex items-center gap-1.5"
                    >
                        <Folder className="h-4 w-4" />
                        <span className="hidden sm:inline">{t("workflowIndex.viewMode.groups")}</span>
                    </Button>
                </div>
            )}
        </div>
    );
}
